import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  ViewEncapsulation,
  forwardRef,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputNativeDirective } from '../../../directives/input-native.directive';
import { IconComponent } from '../../icon/icon.component';

/**
 * InputComponent - A stylized input wrapper with optional leading/trailing icons
 *
 * Implements ControlValueAccessor to work seamlessly with ngModel and reactive forms.
 * This allows the component to work in two scenarios:
 *
 * SCENARIO 1: With ngModel on the wrapper (RECOMMENDED for form fields)
 * Allows ngModel to be placed on the wrapper, making it a true form control:
 * ```html
 * <app-input [(ngModel)]="value" name="field" type="text">
 *   <input class="form-input" type="text" />
 * </app-input>
 * ```
 * The wrapper manages the actual input element value via ControlValueAccessor.
 * This is the proper Angular pattern for custom form controls.
 *
 * SCENARIO 2: With ngModel on projected input (backward compatible)
 * For cases where icon support isn't needed and a raw input is preferred:
 * ```html
 * <app-input>
 *   <input [(ngModel)]="value" name="field" class="form-input" type="text" />
 * </app-input>
 * ```
 * Works as before - the input handles its own ngModel binding.
 * The wrapper just provides styling and optional icons.
 *
 * @implements ControlValueAccessor
 */
@Component({
  selector: 'app-input',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './input.component.html',
  styleUrl: './input.component.css',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, AfterContentInit {
  /**
   * Reference to the child input element (if it exists)
   *
   * This queries the projected content for an <input> element using the
   * InputNativeDirective marker. This directive is automatically applied
   * to all <input> elements (except hidden inputs).
   *
   * If the parent uses: <app-input [(ngModel)]="value"><input /></app-input>
   * Then this will get a reference to that input element, allowing the
   * wrapper component to implement ControlValueAccessor and manage its value.
   *
   * If ngModel is on the input element itself, this reference is still captured
   * but the ControlValueAccessor won't be used (Angular prioritizes directive on input).
   */
  @ContentChild(InputNativeDirective, { read: ElementRef })
  inputElement: ElementRef<HTMLInputElement> | undefined;

  /**
   * Optional icon to display before the input field (e.g., 'search')
   */
  leadingIcon = input<string | undefined>(undefined);

  /**
   * Optional icon to display after the input field (e.g., 'x' for clear)
   */
  trailingIcon = input<string | undefined>(undefined);

  /**
   * Whether to hide the trailing icon (using visibility: hidden) while still reserving space
   * Useful for conditional visibility without layout shift
   */
  trailingIconHidden = input<boolean>(false);

  /**
   * Event emitted when trailing icon is clicked
   */
  trailingIconClick = output<void>();

  /**
   * ControlValueAccessor callbacks - used for both ngModel and reactive forms
   */
  private onChange: (value: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  /**
   * Flag to track if this component is managing the input
   * (i.e., has ControlValueAccessor registered with a form)
   */
  private isControlledByForm = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterContentInit(): void {
    // Set up event listeners on the projected input element if it exists
    // This ensures bidirectional binding works correctly
    if (this.inputElement && this.isControlledByForm) {
      const nativeInput = this.inputElement.nativeElement;

      // Listen for input changes and notify the form
      nativeInput.addEventListener('input', (event: Event) => {
        const target = event.target as HTMLInputElement;
        this.onChange(target.value);
      });

      // Listen for blur events and notify the form
      nativeInput.addEventListener('blur', () => {
        this.onTouched();
      });
    }
  }

  /**
   * ControlValueAccessor implementation - Called when ngModel/FormControl
   * value changes in the parent form
   *
   * This updates the child input element's value when the parent's bound
   * property changes.
   *
   * Example:
   * Parent: <app-input [(ngModel)]="firstName" ...>
   * Parent changes: this.firstName = "John"
   * Result: writeValue("John") is called, updating the input element
   *
   * @param value The new value to set in the input element
   */
  writeValue(value: unknown): void {
    if (this.inputElement && this.isControlledByForm) {
      const nativeInput = this.inputElement.nativeElement;
      nativeInput.value = String(value ?? '');
    }
  }

  /**
   * ControlValueAccessor implementation - Register the onChange callback
   *
   * Called by Angular's FormsModule when setting up the ngModel binding.
   * Stores the callback so we can notify the form of input changes.
   *
   * @param fn Function to call with the new input value when it changes
   */
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
    this.isControlledByForm = true;
  }

  /**
   * ControlValueAccessor implementation - Register the onTouched callback
   *
   * Called by Angular's FormsModule when setting up the ngModel binding.
   * Stores the callback so we can notify the form when input is blurred.
   *
   * @param fn Function to call when the input is blurred (marked as touched)
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * ControlValueAccessor implementation - Disable/enable the input element
   *
   * Called by Angular when the form control's disabled state changes,
   * or when the parent form is disabled.
   *
   * @param isDisabled True to disable the input, false to enable
   */
  setDisabledState(isDisabled: boolean): void {
    if (this.inputElement) {
      this.inputElement.nativeElement.disabled = isDisabled;
    }
  }

  /**
   * Handle trailing icon click
   */
  onTrailingIconClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.trailingIconClick.emit();
  }
}
